import { z } from 'zod'

import { isApprovedStagingUrl, STAGING_SUPABASE_URL } from './staging'

/**
 * Browser environment configuration.
 *
 * Only `VITE_`-prefixed variables reach the bundle. Everything the server
 * needs (OpenAI, Azure Speech, the Supabase service-role key, the cron
 * secret) lives in Supabase secrets and must never appear here — see
 * `AI_Learning_Partner_Requirements_v1_4.md` SEC-A and `CLAUDE.md`.
 *
 * Validation is strict and fails fast at startup: a misconfigured deployment
 * should be obvious immediately rather than surfacing as a confusing runtime
 * error inside a learner session.
 */

/**
 * `staging`: the invite-only hosted staging project for the PO's family test;
 * valid only with the exact approved project URL (`./staging.ts`).
 */
export const APP_ENVIRONMENTS = ['local', 'development', 'staging', 'pilot'] as const
export type AppEnvironment = (typeof APP_ENVIRONMENTS)[number]

export const LEARNING_SERVICES = ['supabase', 'mock'] as const
export type LearningServiceKind = (typeof LEARNING_SERVICES)[number]

/** A Supabase stack on this machine (`supabase start`), never a hosted project. */
export function isLocalSupabaseUrl(url: string): boolean {
  return /^http:\/\/(127\.0\.0\.1|localhost)(:\d+)?(\/|$)/.test(url)
}

/**
 * Substrings that must never appear in a client-visible variable name.
 *
 * Vite's `VITE_` prefix is the only thing standing between a server secret and
 * the public bundle, and it is one typo wide. This list makes
 * `VITE_OPENAI_API_KEY` a startup failure instead of a published credential.
 * Enforces SEC-A and the AUTH-04 acceptance test.
 */
const FORBIDDEN_NAME_FRAGMENTS = [
  'SERVICE_ROLE',
  'SERVICEROLE',
  'SECRET',
  'OPENAI',
  'AZURE',
  'PRIVATE_KEY',
  'PASSWORD',
  'CRON',
] as const

/**
 * Names explicitly allowed despite matching a forbidden fragment. Empty today;
 * kept so an exemption has to be a deliberate, reviewed edit.
 */
const ALLOWED_EXCEPTIONS: ReadonlySet<string> = new Set<string>()

export const envSchema = z.object({
  VITE_SUPABASE_URL: z
    .url('VITE_SUPABASE_URL must be a valid URL')
    .refine(
      (value) =>
        value.startsWith('https://') ||
        value.startsWith('http://127.0.0.1') ||
        value.startsWith('http://localhost'),
      'VITE_SUPABASE_URL must use https, or target a local Supabase stack',
    ),

  VITE_SUPABASE_ANON_KEY: z.string().min(1, 'VITE_SUPABASE_ANON_KEY is required'),

  VITE_APP_ENV: z.enum(APP_ENVIRONMENTS).default('local'),

  VITE_APP_VERSION: z.string().min(1).default('dev'),

  /**
   * Learning backend for local/development builds (decision D-02,
   * first-learning-journey §6.1). `supabase` (default) uses the real RPCs,
   * only against a local Supabase stack; `mock` must be chosen explicitly for
   * a simulated demonstration. Pilot builds have no lessons either way.
   */
  VITE_LEARNING_SERVICE: z.enum(LEARNING_SERVICES).default('supabase'),

  /** Public Sentry DSN. Optional: error reporting is not wired up in Phase 1. */
  VITE_SENTRY_DSN: z
    .url()
    .optional()
    .or(z.literal('').transform(() => undefined)),
})

export type AppEnv = z.infer<typeof envSchema>

export class EnvironmentValidationError extends Error {
  readonly issues: readonly string[]

  constructor(issues: readonly string[]) {
    super(
      `Invalid environment configuration:\n${issues.map((i) => `  - ${i}`).join('\n')}`,
    )
    this.name = 'EnvironmentValidationError'
    this.issues = issues
  }
}

/**
 * Reject client-visible variables whose name suggests a server secret.
 *
 * Only the *name* is inspected and only the name is reported. Values are never
 * read, logged or included in the error message.
 */
export function assertNoServerSecretNames(raw: Record<string, unknown>): void {
  const offenders = Object.keys(raw)
    .filter((key) => key.startsWith('VITE_'))
    .filter((key) => !ALLOWED_EXCEPTIONS.has(key))
    .filter((key) => {
      const upper = key.toUpperCase()
      return FORBIDDEN_NAME_FRAGMENTS.some((fragment) => upper.includes(fragment))
    })

  if (offenders.length > 0) {
    throw new EnvironmentValidationError(
      offenders.map(
        (key) =>
          `${key} looks like a server secret but is exposed to the browser. ` +
          `Move it to Supabase secrets and remove the VITE_ prefix (SEC-A).`,
      ),
    )
  }
}

/**
 * Pure parser — the unit of behaviour that tests exercise directly.
 * `stagingUrl` is the pinned staging project URL; a parameter for tests only.
 */
export function parseEnv(
  raw: Record<string, unknown>,
  stagingUrl: string | null = STAGING_SUPABASE_URL,
): AppEnv {
  assertNoServerSecretNames(raw)

  const result = envSchema.safeParse(raw)
  if (!result.success) {
    throw new EnvironmentValidationError(
      result.error.issues.map((issue) => {
        const path = issue.path.join('.')
        return path.length > 0 ? `${path}: ${issue.message}` : issue.message
      }),
    )
  }

  const env = result.data
  if (env.VITE_APP_ENV === 'staging') {
    const issues: string[] = []
    if (!isApprovedStagingUrl(env.VITE_SUPABASE_URL, stagingUrl))
      issues.push(
        'VITE_APP_ENV=staging requires VITE_SUPABASE_URL to be exactly the approved ' +
          'staging project URL (src/platform/config/staging.ts)',
      )
    if (env.VITE_LEARNING_SERVICE === 'mock')
      issues.push('VITE_LEARNING_SERVICE=mock is not allowed in staging (D-02)')
    if (issues.length > 0) throw new EnvironmentValidationError(issues)
  }
  return env
}

let cached: AppEnv | undefined

/**
 * Validated environment for the running app.
 *
 * Memoized rather than validated at module load so that importing this module
 * in a test does not execute validation as a side effect. The application
 * calls this first thing during bootstrap, which is what makes it fail fast.
 */
export function getEnv(): AppEnv {
  cached ??= parseEnv(import.meta.env)
  return cached
}

/** Test-only hook. */
export function resetEnvCache(): void {
  cached = undefined
}

export function isPilot(env: AppEnv): boolean {
  return env.VITE_APP_ENV === 'pilot'
}

/** The approved hosted staging project (exact URL), never any other hosted project. */
export function isHostedStaging(
  env: AppEnv,
  stagingUrl: string | null = STAGING_SUPABASE_URL,
): boolean {
  return (
    env.VITE_APP_ENV === 'staging' &&
    isApprovedStagingUrl(env.VITE_SUPABASE_URL, stagingUrl)
  )
}
